/**
 * Capture type detection for the Notebook's one capture bar (card
 * quWhYyrNiIbt item 1, PRD §6.2 C2).
 *
 * The bar replaced two composers (a typed quick-add and a link paste form),
 * so the type is no longer implied by which composer is open — it is read off
 * what the person typed, and the chip that shows it stays editable.
 *
 * Rules, in order:
 *   - any URL (http(s):// or a bare www.host.tld)      -> link
 *   - a price, or gift vocabulary                     -> gift
 *   - everything else is plain text                   -> note
 *
 * A note is the honest default: it is the one type that carries words and
 * nothing else, so a wrong guess costs nothing (the person can flip the chip).
 * Kept as a pure module so `scripts/check-capture-type.mjs` can gate it
 * without a browser.
 */
import type { NotebookEntryType as EntryType } from '../../../../shared/types';

const URL_RE = /\b(?:https?:\/\/|www\.)[^\s]+/i;
const PRICE_RE = /(?:[$€£]\s?\d|\b\d+(?:[.,]\d{2})?\s?(?:dollars|bucks|euros|pounds)\b)/i;
const GIFT_RE = /\b(?:gift|present|birthday|anniversary|christmas|wishlist|wish list|for (?:him|her|them))\b/i;

export function detectCaptureType(text: string): EntryType {
  const t = text.trim();
  if (!t) return 'note';
  if (URL_RE.test(t)) return 'link';
  if (PRICE_RE.test(t) || GIFT_RE.test(t)) return 'gift';
  return 'note';
}

/** The URL inside a capture, if there is one — the save path needs the bare
 *  link, and the words around it travel as its description. */
export function urlInCapture(text: string): string | null {
  const m = text.match(/\bhttps?:\/\/[^\s]+/i);
  return m ? m[0] : null;
}
